/// <amd-module name="Pipeline/Analytics/PipelineOutcome/PipelineOutcomeHub/Pipeline.OutcomeHub" />
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import * as ReactDOM from "react-dom";
import { ContextType, IReportContext, Period, TrendBy } from "Analytics/Shared/Common/Types";
import { getDefinitionIdFromUrl } from "Analytics/Shared/Utils/URLHelper";
import { PipelineAnalyticsPage } from "Pipeline/Analytics/Common/Components/PipelineAnalyticsPage";
import { enableSixMonthReportsFF } from "Pipeline/Analytics/Common/Constants";
import * as PerfScenarios from "Pipeline/Analytics/Common/PerfScenarios";
import { IPipelineAnalyticsTelemetryService } from "Pipeline/Analytics/Common/Telemetry/PipelineAnalyticsTelemetryService";
import * as TelemetryConstants from "Pipeline/Analytics/Common/Telemetry/TelemetryConstants";
import { IPipelineOutcomeData, IPipelineOutcomeReportConfiguration, IStageMetrics } from "Pipeline/Analytics/Common/Types";
import { PipelineAnalyticsHeader } from "Pipeline/Analytics/PipelineAnalyticsHeader/Pipeline.AnalyticsHeader";
import { PipelineOutcomeFailedRunsActions } from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Actions/PipelineOutcomeFailedRunsActions";
import { PipelineOutcomeFailedRunsActionsCreator } from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Actions/PipelineOutcomeFailedRunsActionsCreator";
import { PipelineOutcomeFailedRunsCard } from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/ControllerViews/PipelineOutcomeFailedRunsCard";
import { PipelineOutcomeFailedRunsStore } from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Stores/PipelineOutcomeFailedRunsStore";
import { PipelineOutcomeFailingTaskGridActions } from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Actions/PipelineOutcomeFailingTaskGridActions";
import { PipelineOutcomeFailingTaskGridActionsCreator } from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Actions/PipelineOutcomeFailingTaskGridActionsCreator";
import { PipelineOutcomeFailingTaskGridStore } from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Stores/PipelineOutcomeFailingTaskGridStore";
import { PipelineOutcomeFailureTrendActions } from "Pipeline/Analytics/PipelineOutcome/FailureTrendCard/Actions/PipelineOutcomeFailureTrendActions";
import { PipelineOutcomeFailureTrendActionsCreator } from "Pipeline/Analytics/PipelineOutcome/FailureTrendCard/Actions/PipelineOutcomeFailureTrendActionsCreator";
import { PipelineOutcomeFailureTrendCard } from "Pipeline/Analytics/PipelineOutcome/FailureTrendCard/ControllerViews/PipelineOutcomeFailureTrendCard";
import { PipelineOutcomeFailureTrendStore } from "Pipeline/Analytics/PipelineOutcome/FailureTrendCard/Stores/PipelineOutcomeFailureTrendStore";
import { PipelineOutcomePassRateTrendActions } from "Pipeline/Analytics/PipelineOutcome/PassRateTrendCard/Actions/PipelineOutcomePassRateTrendActions";
import { PipelineOutcomePassRateTrendCard } from "Pipeline/Analytics/PipelineOutcome/PassRateTrendCard/ControllerViews/PipelineOutcomePassRateTrendCard";
import { PipelineOutcomePassRateTrendStore } from "Pipeline/Analytics/PipelineOutcome/PassRateTrendCard/Stores/PipelineOutcomePassRateTrendStore";
import { PipelineOutcomeHubActionsCreator } from "Pipeline/Analytics/PipelineOutcome/PipelineOutcomeHub/Actions/PipelineOutcomeHubActionsCreator";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/PipelineOutcomeHub/Resources";
import * as React from "react";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

export interface IOutcomeAnalyticsPageState {
    definitionName: string;
    passPercentage: number;
    totalPipelineCount: number;
    errorText?: string;
    selectedStage?: IStageMetrics;
}

export class PipelineOutcomeAnalyticsPage extends VssComponent<IVssComponentProperties, IOutcomeAnalyticsPageState> {
    private _reportContext: IReportContext;
    private _pipelineOutcomeHubActionsCreator: PipelineOutcomeHubActionsCreator;

    private _failureTrendStore: PipelineOutcomeFailureTrendStore;
    private _passRateTrendStore: PipelineOutcomePassRateTrendStore;
    private _failedRunsStore: PipelineOutcomeFailedRunsStore;
    private _failingTaskGridStore: PipelineOutcomeFailingTaskGridStore;

    private _failureTrendActionsCreator: PipelineOutcomeFailureTrendActionsCreator;
    private _failedRunsActionsCreator: PipelineOutcomeFailedRunsActionsCreator;
    private _failingTaskGridActionsCreator: PipelineOutcomeFailingTaskGridActionsCreator;

    private _defaultPeriod: Period = Period.Days_14;

    private _hasPassRateTrendRendered = false;
    private _hasPipelineFailureTrendRendered = false;
    private _hasFailedRunsRendered = false;
    private _reportRenderCount = 0;

    private _perfService: IVssPerformanceService;

    private telemetryService: IPipelineAnalyticsTelemetryService;

    private PassRateTrendCardRef = React.createRef<PipelineOutcomePassRateTrendCard>();
    private FailureTrendCardRef = React.createRef<PipelineOutcomeFailureTrendCard>();
    private FailedRunsCardRef = React.createRef<PipelineOutcomeFailedRunsCard>();

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);
        this.state = {} as IOutcomeAnalyticsPageState;
        
        if (isFeatureFlagEnabled(context.pageContext, PipelineEntities.RenameBuildEntitiesFeatureFlag)) {
            PipelineEntities.switchEntityAndAttributeNames();
        }

        const _failingTaskGridActions = new PipelineOutcomeFailingTaskGridActions();
        this._failingTaskGridActionsCreator = new PipelineOutcomeFailingTaskGridActionsCreator(_failingTaskGridActions, context.pageContext);
        this._failingTaskGridStore = new PipelineOutcomeFailingTaskGridStore(_failingTaskGridActions);

        const passRateTrendCardActions = new PipelineOutcomePassRateTrendActions();
        this._passRateTrendStore = new PipelineOutcomePassRateTrendStore(passRateTrendCardActions);

        const failedRunsCardActions = new PipelineOutcomeFailedRunsActions();
        this._failedRunsStore = new PipelineOutcomeFailedRunsStore(failedRunsCardActions);
        this._failedRunsActionsCreator = new PipelineOutcomeFailedRunsActionsCreator(
            failedRunsCardActions,
            this._failingTaskGridActionsCreator,
            context.pageContext
        );

        const failureTrendChartActions = new PipelineOutcomeFailureTrendActions();
        this._failureTrendStore = new PipelineOutcomeFailureTrendStore(failureTrendChartActions);
        this._failureTrendActionsCreator = new PipelineOutcomeFailureTrendActionsCreator(failureTrendChartActions, context.pageContext);

        this._perfService = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._pipelineOutcomeHubActionsCreator = new PipelineOutcomeHubActionsCreator(
            passRateTrendCardActions,
            failureTrendChartActions,
            context.pageContext
        );

        const definitionId = this._getBuildDefinitionId();
        this._reportContext = {
            contextType: ContextType.Build,
            definitionId: definitionId
        } as IReportContext;

        this.telemetryService = context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
        this.telemetryService.initialize(definitionId);
    }

    public componentWillUnmount() {
        this._passRateTrendStore.dispose();
        this._failedRunsStore.dispose();
        this._failingTaskGridStore.dispose();
        this._failureTrendStore.dispose();
        super.componentWillUnmount();
    }
    public componentDidUpdate() {
        if (this.PassRateTrendCardRef !== undefined && this.PassRateTrendCardRef.current) {
            const PassRateTrendCardElement = ReactDOM.findDOMNode(this.PassRateTrendCardRef.current) as Element;
            if(PassRateTrendCardElement){
                PassRateTrendCardElement.setAttribute("tabindex","0");
            }
        }
        if (this.FailureTrendCardRef !== undefined && this.FailureTrendCardRef.current) {
            const FailureTrendCardElement = ReactDOM.findDOMNode(this.FailureTrendCardRef.current) as Element;
            if(FailureTrendCardElement){
                FailureTrendCardElement.setAttribute("tabindex","0");
            };
        }
        if (this.FailedRunsCardRef !== undefined && this.FailedRunsCardRef.current) {
            const FailedRunsCardElement = ReactDOM.findDOMNode(this.FailedRunsCardRef.current) as Element;
            if(FailedRunsCardElement){
                FailedRunsCardElement.setAttribute("tabindex","0");
            };
        }
    }

    public render() {
        return (
            <PipelineAnalyticsPage
                renderHeader={this._renderHeader}
                getDataValidationPromises={this._getDataValidationPromises}
                onSetPipelineName={this._handleSetPipelineName}
                onError={this._handleError}
            >
                <div className="pipeline-outcome-report-view flex-grow flex-column">
                    {
                        <>
                            <PipelineOutcomePassRateTrendCard
                                className="pipeline-outcome-trend-card pass-rate-trend-card flex-column"
                                titleClassName="pipeline-passrate-card-title"
                                headerClassName="pipeline-card-title"
                                store={this._passRateTrendStore}
                                onDataRendered={this._onPassRateTrendRendered}
                                updatePipelineData={this._updatePipelineData}
                                ref = {this.PassRateTrendCardRef}
                            />
                            {this.state.totalPipelineCount > 0 && this.state.passPercentage < 100 && (
                                <>
                                    <PipelineOutcomeFailureTrendCard
                                        className="pipeline-outcome-trend-card failure-rate-trend-card flex-column"
                                        headerClassName="pipeline-card-title"
                                        store={this._failureTrendStore}
                                        onDataRendered={this._onPipelineFailureTrendRendered}
                                        ref = {this.FailureTrendCardRef}
                                    />
                                    <PipelineOutcomeFailedRunsCard
                                        className="pipeline-failedruns-card flex-column"
                                        titleClassName="pipeline-passrate-card-title"
                                        headerClassName="pipeline-card-title"
                                        failingTaskGridStore={this._failingTaskGridStore}
                                        store={this._failedRunsStore}
                                        failingTaskGridActionsCreator={this._failingTaskGridActionsCreator}
                                        actionCreator={this._failedRunsActionsCreator}
                                        onDataRendered={this._onFailedRunsRendered}
                                        reportContext={this._reportContext}
                                        ref = {this.FailedRunsCardRef}
                                    />
                                </>
                            )}
                        </>
                    }
                </div>
            </PipelineAnalyticsPage>
        );
    }

    private _renderHeader = (): JSX.Element => {
        return (
            <PipelineAnalyticsHeader
                headerTitle={Resources.PipelinePassRateReportHeaderText}
                definitionName={this.state.definitionName}
                definitionId={this._reportContext.definitionId}
                onPeriodChanged={this._onPeriodChanged}
                onBranchChanged={this._onBranchChanged}
                maxPeriod={isFeatureFlagEnabled(this.context.pageContext, enableSixMonthReportsFF, false) ? Period.Days_180 : Period.Days_30}
            />
        );
    };

    private _getDataValidationPromises = (): Promise<any>[] => {
        const confValues = {
            period: this._defaultPeriod,
            trendBy: TrendBy.Days
        } as IPipelineOutcomeReportConfiguration;
        return [
            this._pipelineOutcomeHubActionsCreator.updateConfigurationValues(this._reportContext, confValues),
            this._failureTrendActionsCreator.updateConfigurationValues(this._reportContext, confValues),
            this._failedRunsActionsCreator.updateConfigurationValues(this._reportContext, confValues)
        ];
    };

    private _handleSetPipelineName = (pipelineName: string) => {
        this.setState({ definitionName: pipelineName });
    };

    private _handleError = (errorText: string) => {
        this.setState({ errorText: errorText });
    };

    private _onPassRateTrendRendered = () => {
        this._hasPassRateTrendRendered = true;
        this._logPerformance();
    };

    private _onPipelineFailureTrendRendered = () => {
        this._hasPipelineFailureTrendRendered = true;
        this._logPerformance();
    };

    private _onFailedRunsRendered = () => {
        this._hasFailedRunsRendered = true;
        this._logPerformance();
    };

    private _logPerformance() {
        if (this._hasPassRateTrendRendered && this._hasFailedRunsRendered && this._hasPipelineFailureTrendRendered) {
            if (this._reportRenderCount === 0) {
                this._perfService.markTimeToInteractive(PerfScenarios.Area, PerfScenarios.PipelinesAnalyticsHub);
                this._reportRenderCount++;
            }
        }
    }

    private _getBuildDefinitionId(): number {
        return getDefinitionIdFromUrl(this.context.pageContext);
    }

    private _publishDateFilterChangedTelemetry(period: Period): void {
        this.telemetryService.publishEventWithSingleKey(
            TelemetryConstants.feature_PipelineAnalyticsFailureHubFilter,
            TelemetryConstants.DateFilterChangedValue,
            Period[period]
        );
    }

    private _publishBranchFilterChangedTelemetry(confValues: IPipelineOutcomeReportConfiguration): void {
        this.telemetryService.publishEventWithSingleKey(
            TelemetryConstants.feature_PipelineAnalyticsFailureHubFilter,
            TelemetryConstants.BranchCount,
            confValues.branchItems!.length
        );
    }

    private _onPeriodChanged = (confValues: IPipelineOutcomeReportConfiguration): void => {
        this._updateConfigurationValues(confValues);
        this._publishDateFilterChangedTelemetry(confValues.period);
    };

    private _onBranchChanged = (confValues: IPipelineOutcomeReportConfiguration): void => {
        this._updateConfigurationValues(confValues);
        this._publishBranchFilterChangedTelemetry(confValues);
    };

    private _updateConfigurationValues(confValues: IPipelineOutcomeReportConfiguration) {
        this._pipelineOutcomeHubActionsCreator.updateConfigurationValues(this._reportContext, confValues);
        this._failureTrendActionsCreator.updateFailingStageAndTaskData(this._reportContext, confValues);
        this._failedRunsActionsCreator.updateConfigurationValues(this._reportContext, confValues);
    }

    private _updatePipelineData = (data: IPipelineOutcomeData): void => {
        this.setState({
            passPercentage: data.pipelineOutcomePassRateData.passPercentage,
            totalPipelineCount: data.pipelineOutcomePassRateData.totalCount
        });
    };
}

Components.add("pipelineOutcomeHub", PipelineOutcomeAnalyticsPage);
